/** Texto e identificadores no formato STEP (ISO 10303-21).
 *
 *  Um IFC em texto só admite, dentro das plicas, os caracteres de 32 a
 *  126. Tudo o resto vai escrito por directivas: `\X2\00E7\X0\` é um
 *  "ç". Escrever o "ç" directamente como byte do latin1 funciona em
 *  muitos leitores, mas não é o formato, e há leitores que o recusam
 *  ou o trocam por outro carácter. Ao escrever usamos sempre as
 *  directivas; ao ler aceitamos as duas coisas, porque os ficheiros
 *  reais trazem de tudo.
 *
 *  A ideia de codificar assim ao injectar propriedades vem também do
 *  liteAECO (js/ifc-step-utils.js, Apache-2.0), que o faz pelo mesmo
 *  motivo. O código é nosso. */

/** Lê o conteúdo entre plicas (sem as plicas) e devolve o texto. */
export function descodificarTextoStep(bruto: string): string {
  let s = bruto.replace(/''/g, "'")
  // \X2\hhhh...\X0\ (UCS-2) e \X4\hhhhhhhh...\X0\ (UCS-4).
  s = s.replace(/\\X2\\((?:[0-9A-Fa-f]{4})+)\\X0\\/g, (_, hex: string) => {
    let out = ''
    for (let i = 0; i < hex.length; i += 4) out += String.fromCharCode(parseInt(hex.slice(i, i + 4), 16))
    return out
  })
  s = s.replace(/\\X4\\((?:[0-9A-Fa-f]{8})+)\\X0\\/g, (_, hex: string) => {
    let out = ''
    for (let i = 0; i < hex.length; i += 8) out += String.fromCodePoint(parseInt(hex.slice(i, i + 8), 16))
    return out
  })
  // \X\hh: um byte do ISO 8859-1.
  s = s.replace(/\\X\\([0-9A-Fa-f]{2})/g, (_, hex: string) => String.fromCharCode(parseInt(hex, 16)))
  // \S\c: o carácter c + 128 no alfabeto corrente (ISO 8859-1 por omissão).
  s = s.replace(/\\S\\(.)/g, (_, c: string) => String.fromCharCode(c.charCodeAt(0) + 128))
  // \P?\ muda de alfabeto; não mudamos, só se retira.
  s = s.replace(/\\P[A-I]\\/g, '')
  return s.replace(/\\\\/g, '\\')
}

/** Escreve texto para ir entre plicas (sem as plicas). */
export function codificarTextoStep(texto: string): string {
  let out = ''
  let bloco = ''
  const fecharBloco = () => {
    if (bloco) {
      out += `\\X2\\${bloco}\\X0\\`
      bloco = ''
    }
  }
  for (const ch of texto) {
    const cp = ch.codePointAt(0) ?? 0
    if (cp >= 32 && cp <= 126) {
      fecharBloco()
      if (ch === "'") out += "''"
      else if (ch === '\\') out += '\\\\'
      else out += ch
    } else if (cp > 0xffff) {
      fecharBloco()
      out += `\\X4\\${cp.toString(16).toUpperCase().padStart(8, '0')}\\X0\\`
    } else {
      bloco += cp.toString(16).toUpperCase().padStart(4, '0')
    }
  }
  fecharBloco()
  return out
}

/** Argumento de texto: `'...'`, ou `$` para vazio. */
export function argTexto(texto: string | null | undefined): string {
  return texto === null || texto === undefined ? '$' : `'${codificarTextoStep(texto)}'`
}

/** Lê um argumento de texto; null para `$`, `*` ou o que não é texto. */
export function lerArgTexto(arg: string | undefined): string | null {
  const t = (arg ?? '').trim()
  if (t.length < 2 || t[0] !== "'" || t[t.length - 1] !== "'") return null
  return descodificarTextoStep(t.slice(1, -1))
}

export function lerRef(arg: string | undefined): number | null {
  const t = (arg ?? '').trim()
  if (!/^#\d+$/.test(t)) return null
  return parseInt(t.slice(1), 10)
}

/** Lista de referências `(#1,#2)`; vazia para `$`, `()` ou o que não é lista. */
export function lerListaDeRefs(arg: string | undefined): number[] {
  const t = (arg ?? '').trim()
  if (!t.startsWith('(') || !t.endsWith(')')) return []
  const out: number[] = []
  for (const m of t.slice(1, -1).matchAll(/#(\d+)/g)) out.push(parseInt(m[1], 10))
  return out
}

/** Valor tipado `IFCLABEL('x')` -> { tipo: 'IFCLABEL', interior: "'x'" }. */
export function lerValorTipado(arg: string | undefined): { tipo: string; interior: string } | null {
  const t = (arg ?? '').trim()
  const m = /^([A-Za-z][A-Za-z0-9_]*)\s*\(([\s\S]*)\)$/.exec(t)
  if (!m) return null
  return { tipo: m[1].toUpperCase(), interior: m[2].trim() }
}

const ALFABETO_GUID = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz_$'

/** GlobalId novo: 128 bits aleatórios (UUID v4) comprimidos nos 22
 *  caracteres do IFC. O primeiro carácter leva 2 bits e os outros 21
 *  levam 6 cada, como na documentação da buildingSMART sobre o
 *  GlobalId. `aleatorio` existe para os testes. */
export function novoGlobalId(aleatorio?: () => Uint8Array): string {
  const bytes = aleatorio ? aleatorio() : crypto.getRandomValues(new Uint8Array(16))
  bytes[6] = (bytes[6] & 0x0f) | 0x40
  bytes[8] = (bytes[8] & 0x3f) | 0x80
  let bits = ''
  for (const b of bytes) bits += b.toString(2).padStart(8, '0')
  let guid = ALFABETO_GUID[parseInt(bits.slice(0, 2), 2)]
  for (let i = 2; i < 128; i += 6) guid += ALFABETO_GUID[parseInt(bits.slice(i, i + 6), 2)]
  return guid
}
